// ======================================================
// ProductDetails.jsx – collect inputs & trigger generation
// ======================================================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../assets/styles/ProductDetails.module.css";
import { formDescriptors } from "../data/formDescriptors";
import axios from "axios";

function ProductDetails() {
  const navigate = useNavigate();
  const username = localStorage.getItem("username");
  const password = localStorage.getItem("password");

  /* ---------- local state ---------- */
  const [productName, setProductName] = useState("");
  const [numForms, setNumForms] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);

  const [imageGenUrl, setImageGenUrl] = useState(
    localStorage.getItem("IMAGE_GEN_URL") || ""
  );

  /* ---------- persist URL on change ---------- */
  useEffect(() => {
    localStorage.setItem("IMAGE_GEN_URL", imageGenUrl);
  }, [imageGenUrl]);

  /* ---------- build prompt helper ---------- */
  const buildPrompt = () =>
    `Generate ${numForms} product concept rendering${
      numForms > 1 ? "s" : ""
    } for a ${productName} with ${Object.entries(selectedAttributes)
      .flatMap(([cat, arr]) => arr.map((a) => `${cat}: ${a}`))
      .join(", ")}.`;

  /* ---------- toggle attribute ---------- */
  const toggleAttribute = (cat, attr) => {
    const cur = selectedAttributes[cat] || [];
    const maxTotal = 10,
      maxPerCat = 3;
    const total = Object.values(selectedAttributes).reduce(
      (s, a) => s + a.length,
      0
    );
    if (cur.includes(attr)) {
      setSelectedAttributes({
        ...selectedAttributes,
        [cat]: cur.filter((x) => x !== attr),
      });
    } else {
      if (total >= maxTotal || cur.length >= maxPerCat) return;
      setSelectedAttributes({ ...selectedAttributes, [cat]: [...cur, attr] });
    }
  };

  /* ---------- main handler ---------- */
  const handleGenerateForm = async () => {
    if (!username || !password) {
      alert("Username/password missing – please log in again.");
      navigate("/");
      return;
    }
    if (!productName.trim()) return alert("Please enter a product name.");
    if (!imageGenUrl.trim()) return alert("Please enter IMAGE_GEN_URL.");

    const productFolder = productName.toLowerCase().replace(/\s+/g, "");
    const promptText = buildPrompt();

    try {
      setIsGenerating(true);

      // ensure credentials still valid
      await axios.post("http://localhost:5000/api/auth/start", {
        username,
        password,
      });

      // loop numForms times
      for (let i = 0; i < numForms; i += 1) {
        await axios.post("http://localhost:5000/api/generate", {
          prompt: promptText,
          username,
          productName: productFolder,
          seed: 42 + i,
          imageGenUrl, // ⬅️ send to backend
        });
      }

      localStorage.setItem("productName", productFolder);
      navigate("/form-display");
    } catch (err) {
      console.error(err);
      alert(
        err.response
          ? `Backend error ${err.response.status}: ${err.response.data.message}`
          : "Unexpected error during generation."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const attributes = selectedCategory ? formDescriptors[selectedCategory] : [];

  /* ---------- render ---------- */
  return (
    <div className={styles.container}>
      <h2>Enter Product Details</h2>

      {/* -------- product name -------- */}
      <label>Product Name</label>
      <input
        value={productName}
        onChange={(e) => setProductName(e.target.value)}
        placeholder="e.g. Futuristic Bike"
      />

      {/* -------- num forms -------- */}
      <label>Number of Forms: {numForms}</label>
      <div className={styles.stepper}>
        <button
          disabled={numForms <= 1}
          onClick={() => setNumForms(numForms - 1)}
        >
          –
        </button>
        <span>{numForms}</span>
        <button
          disabled={numForms >= 10}
          onClick={() => setNumForms(numForms + 1)}
        >
          +
        </button>
      </div>

      {/* -------- IMAGE_GEN_URL textbox -------- */}
      <label>Runpod NGROK URL:</label>
      <input
        value={imageGenUrl}
        onChange={(e) => setImageGenUrl(e.target.value)}
        placeholder="https://xxxxx.ngrok-free.app/generate"
      />

      {/* -------- category selector -------- */}
      <label>Select Category</label>
      <select
        value={selectedCategory}
        onChange={(e) => setSelectedCategory(e.target.value)}
      >
        <option value="">-- Choose Category --</option>
        {Object.keys(formDescriptors).map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>

      {/* -------- attribute cards -------- */}
      {selectedCategory && (
        <div className={styles.cardGrid}>
          {attributes.map((attr) => {
            const sel = (selectedAttributes[selectedCategory] || []).includes(
              attr
            );
            return (
              <div
                key={attr}
                className={`${styles.card} ${sel ? styles.selected : ""}`}
                onClick={() => toggleAttribute(selectedCategory, attr)}
              >
                {attr}
              </div>
            );
          })}
        </div>
      )}

      {/* -------- selected attributes summary -------- */}
      {Object.keys(selectedAttributes).length > 0 && (
        <div className={styles.selectedSummary}>
          <h4>Selected Attributes:</h4>
          <ul>
            {Object.entries(selectedAttributes).map(
              ([cat, attrs]) =>
                attrs.length > 0 && (
                  <li key={cat}>
                    <strong>{cat}:</strong> {attrs.join(", ")}
                  </li>
                )
            )}
          </ul>
        </div>
      )}

      {/* summary unchanged ... */}

      <button
        className={styles.generateBtn}
        onClick={handleGenerateForm}
        disabled={isGenerating}
      >
        {isGenerating ? "Generating…" : "Generate Form"}
      </button>
    </div>
  );
}

export default ProductDetails;
// ======================================================
